import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Userpilot } from 'userpilot';

import { Account } from 'types/account';
import APIError from 'types/api_error';

import { API_BASE_URL } from 'services/api';
import { getAuthAPI, postAuthAPI } from 'services/api_auth';
import { REQUEST_HEADERS } from 'services/constants';

export type LoginParams = {
  email: string;
  password: string;
};

export type ResetPasswordParams = {
  password: string;
  token: string;
};

export type AuthenticationContextProps =
  | {
      // FIXME: Rename to account
      user?: Account;
      logout: () => void;
      isLoading: boolean;
      isAuthenticated: boolean;
      loginReset: () => void;
      login: (params: LoginParams) => void;
      samlLogin: () => void;
      loginError?: APIError;
      samlLoginError?: APIError;
      isLoggingIn?: boolean;
      isSamlLoggingIn?: boolean;
      authToken?: string;

      // reset password flow
      setPassword: (params: ResetPasswordParams) => void;
      setPasswordError?: APIError;
      isSettingPassword?: boolean;
    }
  | undefined;

export const AuthenticationContext =
  React.createContext<AuthenticationContextProps>(undefined);

export function useAuthenticationOptional() {
  return React.useContext(AuthenticationContext);
}

export function useAuthentication() {
  const context = React.useContext(AuthenticationContext);

  if (context === undefined) {
    throw new Error(
      '`useAuthentication` can only be used within an `AuthenticationProvider`.'
    );
  }

  return context;
}

type TokenResponse = {
  token: string;
};

const CONSOLE_TOKEN = 'console.token';

export type AuthenticationProviderProps = React.PropsWithChildren<{
  token?: string;
}>;

export function AuthenticationProvider({
  token: initialToken,
  children,
}: AuthenticationProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();

  const [isAuthenticating, setIsAuthenticating] = React.useState(true);
  const [token, setToken] = React.useState<string | undefined>(initialToken);
  const [user, setUser] = React.useState<Account>();

  const logout = React.useCallback(() => {
    setToken(undefined);
    setUser(undefined);
    queryClient.clear();
    localStorage.removeItem(CONSOLE_TOKEN);

    if (window.FrontChat && import.meta.env.VITE_FRONT_CHAT_ID) {
      window.FrontChat('shutdown', { clearSession: true });
    }
  }, [queryClient]);

  const loginMutation = useMutation(
    (params: LoginParams) =>
      postAuthAPI('/internal/auth/login', params).then((res) => res.json()),
    {
      onSuccess(data) {
        handleTokenResponse(data);
      },
      onError() {
        setToken(undefined);
      },
    }
  );

  const samlLoginMutation = useMutation(
    () =>
      getAuthAPI('/internal/auth/saml_login', undefined).then((res) =>
        res.json()
      ),
    {
      onSuccess(data) {
        handleTokenResponse(data, true);
      },
      onError() {
        setToken(undefined);
      },
    }
  );

  const setPasswordMutation = useMutation(
    (params: ResetPasswordParams) =>
      postAuthAPI('/internal/auth/set_password', params).then((res) =>
        res.json()
      ),
    {
      onSuccess(data) {
        handleTokenResponse(data);
      },
    }
  );

  async function handleTokenResponse(data: TokenResponse, isSAML?: boolean) {
    try {
      const { account_id } = JSON.parse(atob(data.token.split('.')[1]));

      localStorage.setItem(CONSOLE_TOKEN, data.token);
      setToken(data.token);

      queryClient.invalidateQueries(['account']);
      const options = {
        headers: {
          ...REQUEST_HEADERS,
          Authorization: `Bearer ${data.token}`,
        },
      };

      const account: Account = await fetch(
        `${API_BASE_URL}/internal/accounts/${account_id}`,
        options
      ).then((res) => res.json());

      const frontHash: { front_chat_user_hash: string } = await fetch(
        `${API_BASE_URL}/internal/accounts/${account_id}/front_chat_user_hash`,
        options
      ).then((res) => res.json());

      setUser(account);
      setIsAuthenticating(false);

      if (location.state?.from || isSAML) {
        navigate(location.state?.from ?? '/', { replace: true });
      }

      // Third-party identification
      if (account) {
        Userpilot.identify(account_id, {
          name: account.name ?? account.email,
          email: account.email,
          created_at: account.created_at,
          isBilling: account.roles.some(({ name }) => name === 'billing'),
        });

        const chatId = import.meta.env.VITE_FRONT_CHAT_ID;

        if (window.FrontChat && frontHash && chatId) {
          window.FrontChat('init', {
            chatId,
            useDefaultLauncher: false,
            name: account.name,
            email: account.email,
            userHash: frontHash.front_chat_user_hash,
          });
        }
      }
    } catch (error: unknown) {
      // Handle invalid token errors
      if (
        error instanceof SyntaxError ||
        (error instanceof Error && error.message.includes('Invalid token'))
      ) {
        setToken(undefined);
        setUser(undefined);
        localStorage.removeItem(CONSOLE_TOKEN);
        setIsAuthenticating(false);
      }
    }

    queryClient.invalidateQueries(['account']);
  }

  // To be used when the JWT gotten from /login or a previous /refresh_session call expires.
  const { mutate: refreshSession } = useMutation<TokenResponse>(
    () => {
      const token = localStorage.getItem(CONSOLE_TOKEN);

      if (token) {
        return postAuthAPI('/internal/auth/refresh_session', undefined, {
          token,
        }).then((res) => res.json());
      }

      return Promise.reject('Not authenticated');
    },
    {
      onSuccess(data) {
        handleTokenResponse(data);
      },
      onError() {
        logout();
        setIsAuthenticating(false);
      },
    }
  );

  // Check if user has an active session.
  // Keep authenticated if session is active, otherwise mark as signed out.
  React.useEffect(() => {
    if (isAuthenticating && !token) {
      refreshSession();
    }
  }, [isAuthenticating, token, refreshSession]);

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        login: loginMutation.mutateAsync,
        samlLogin: samlLoginMutation.mutate,
        loginError: loginMutation.error as APIError | undefined,
        samlLoginError: samlLoginMutation.error as APIError | undefined,
        isLoggingIn: loginMutation.isLoading,
        isSamlLoggingIn: samlLoginMutation.isLoading,
        loginReset: loginMutation.reset,
        logout,
        authToken: token,
        isLoading: isAuthenticating,
        isAuthenticated: Boolean(token),

        // reset password flow
        setPassword: setPasswordMutation.mutate,
        setPasswordError: setPasswordMutation.error as APIError,
        isSettingPassword: setPasswordMutation.isLoading,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
}
